// @ts-check
/**
 * Metriky písania.
 *
 * Slovenská/česká školská metrika je "čistá rýchlosť":
 *     čistá rýchlosť (úhozy/min) = hrubé úhozy/min − (chyby/min × koeficient)
 * Koeficient 10 je bežný, ale školy a súťaže sa líšia, preto je konfigurovateľný
 * (`netPenalty`) a v UI sa dá prepnúť.
 */

export const DEFAULT_NET_PENALTY = 10;

/** @param {number} ms */
export function minutesOf(ms) {
  return ms / 60000;
}

/** @param {number} v */
function r1(v) {
  return Math.round(v * 10) / 10;
}

/**
 * Základný znak bez diakritiky (ô → o, ľ → l, š → s, ...).
 * Používa sa pri zhovievavom režime diakritiky.
 * @param {string} ch
 */
export function normalizeBase(ch) {
  return ch.normalize('NFD').replace(/\p{M}+/gu, '').normalize('NFC');
}

/**
 * @typedef {Object} MetricsInput
 * @property {number} grossStrokes  všetky započítané úhozy (správne aj chybné)
 * @property {number} correct        správne úhozy
 * @property {number} errors         chybné úhozy a vynechané znaky
 * @property {number} [softErrors]   chyby diakritiky v zhovievavom režime
 * @property {number} durationMs
 * @property {number} [netPenalty]
 */

/**
 * @typedef {Object} Metrics
 * @property {number} grossCpm
 * @property {number} netCpm
 * @property {number} wpm
 * @property {number} accuracy      0..1 (presnosť = 100 − chybovosť)
 * @property {number} errorRatePercent chybovosť v percentách (podľa predlohy: chyby / hrubé údery)
 * @property {number} errorsPerMin
 * @property {number} softErrors
 * @property {number} durationMs
 */

/**
 * @param {MetricsInput} input
 * @returns {Metrics}
 */
export function computeMetrics(input) {
  const {
    grossStrokes, correct, errors, softErrors = 0, durationMs,
    netPenalty = DEFAULT_NET_PENALTY,
  } = input;

  const minutes = minutesOf(durationMs);
  const valid = minutes > 0 && Number.isFinite(minutes);

  const grossCpm = valid ? grossStrokes / minutes : 0;
  // Oficiálne: od hrubých úderov sa odpočíta 10 trestných bodov za každú chybu.
  const netCpm = valid ? Math.max(0, (grossStrokes - errors * netPenalty) / minutes) : 0;
  const wpm = valid ? correct / 5 / minutes : 0;

  // Chybovosť podľa predlohy (kap. 13.3): chyby / hrubé údery. Presnosť je jej doplnok.
  const errorRatePercent = grossStrokes > 0 ? (errors / grossStrokes) * 100 : 0;

  return {
    grossCpm: r1(grossCpm),
    netCpm: r1(netCpm),
    wpm: r1(wpm),
    accuracy: 1 - errorRatePercent / 100,
    errorRatePercent: Math.round(errorRatePercent * 100) / 100,
    errorsPerMin: valid ? r1(errors / minutes) : 0,
    softErrors,
    durationMs,
  };
}

/**
 * Formátovanie trvania pre UI.
 * @param {number} ms
 */
export function formatDuration(ms) {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** @param {number} v */
export function formatPercent(v) {
  return `${Math.round(v * 100)} %`;
}

/**
 * Hviezdičky za lekciu (1–3) podľa dosiahnutých cieľov.
 * @param {Metrics} m
 * @param {{cpm: number, accuracy: number}} targets
 */
export function starsFor(m, targets) {
  const cpmTarget = targets?.cpm ?? 120;
  const accTarget = targets?.accuracy ?? 0.97;
  let stars = 0;
  if (m.netCpm >= cpmTarget * 0.6 && m.accuracy >= Math.max(0.8, accTarget - 0.1)) stars = 1;
  if (m.netCpm >= cpmTarget * 0.85 && m.accuracy >= accTarget - 0.03) stars = 2;
  if (m.netCpm >= cpmTarget && m.accuracy >= accTarget) stars = 3;
  return stars;
}
